import { Link } from "react-router-dom";
import SectionEyebrow from "@/components/shared/SectionEyebrow";
import useSeo from "@/lib/useSeo";

const SECTIONS = [
  {
    h: "1. Information We Collect",
    p: "IslandHop Technologies LLC (\"IslandHop\", \"we\", \"us\") collects information you provide directly — such as your name, email address, phone number, delivery address, and payment details — when you create an account, place an order, sign up as a driver, or apply as a merchant partner. We also automatically collect device, app usage, location, and log data.",
  },
  {
    h: "2. How We Use Your Information",
    p: "We use your information to operate the IslandHop platform, fulfill deliveries, process payments, prevent fraud, communicate service updates, improve our services, and comply with applicable laws in the United States, Trinidad & Tobago, and the wider Caribbean region.",
  },
  {
    h: "3. SMS / Text Message Notifications",
    p: "If you opt in by checking the SMS consent checkbox on any of our forms, you agree to receive automated transactional text messages from IslandHop Technologies LLC at the phone number you provided. These messages may include application status updates, onboarding instructions, delivery notifications, driver assignments, and account-related alerts. Message frequency varies. Message and data rates may apply. You may opt out at any time by replying STOP to any message; reply HELP for help. Opting out of SMS will not affect the essential operation of your account, but you may not receive time-sensitive updates. Mobile phone numbers and SMS consent data are never shared with third parties or affiliates for marketing purposes.",
  },
  {
    h: "4. Sharing of Information",
    p: "We share necessary information with independent driver contractors, merchant partners, payment processors, and service providers strictly to deliver our services. We do not sell your personal data. Phone numbers collected for SMS notifications are not shared with third parties for their own marketing.",
  },
  {
    h: "5. Data Security",
    p: "We apply industry-standard encryption, access controls, and PCI-compliant payment processing. While no method of transmission is 100% secure, we work continuously to protect your information.",
  },
  {
    h: "6. Your Rights",
    p: "You can request access to, correction of, or deletion of your personal data at any time. To make a request, email privacy@islandhoptt.com, or visit our /delete-account page for account deletion.",
  },
  {
    h: "7. Children's Privacy",
    p: "IslandHop is not directed to children under the age of 13, and we do not knowingly collect personal information from children under 13.",
  },
  {
    h: "8. Changes to this Policy",
    p: "We may update this Privacy Policy from time to time. Material changes will be communicated by posting the updated policy on this page with a revised \"Last updated\" date.",
  },
  {
    h: "9. Contact Us",
    p: "Questions about this Privacy Policy? Reach out to support@islandhoptt.com or privacy@islandhoptt.com. IslandHop Technologies LLC — Port of Spain, Trinidad & Tobago.",
  },
];

export default function PrivacyPolicy() {
  useSeo({
    title: "Privacy Policy",
    description:
      "IslandHop Technologies LLC Privacy Policy — how we collect, use, share and protect your personal data, including SMS notification preferences.",
  });

  return (
    <div data-testid="privacy-policy-page">
      <section className="relative pt-20 lg:pt-28 pb-12 border-b border-ih-line overflow-hidden">
        <div className="absolute inset-0 ih-grid-bg opacity-30" />
        <div className="relative max-w-[1400px] mx-auto px-5 lg:px-10 space-y-5">
          <SectionEyebrow label="Privacy" color="electric" />
          <h1 className="font-display font-black text-5xl lg:text-6xl text-white">Privacy Policy</h1>
          <p className="text-white/40 font-mono text-xs uppercase tracking-[0.18em]">
            Last updated · February 2026
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-3xl mx-auto px-5 lg:px-10 space-y-8">
          {SECTIONS.map((s, i) => (
            <div key={i} className="space-y-3">
              <h2 className="font-display font-bold text-2xl text-white">{s.h}</h2>
              <p className="text-white/70 leading-relaxed">{s.p}</p>
            </div>
          ))}

          <div className="mt-12 pt-8 border-t border-ih-line flex flex-wrap gap-3">
            <Link to="/privacy-policy" className={chip(true)} data-testid="legal-tab-privacy">
              Privacy Policy
            </Link>
            <Link to="/terms-and-conditions" className={chip(false)} data-testid="legal-tab-terms">
              Terms &amp; Conditions
            </Link>
            <Link to="/refund-policy" className={chip(false)} data-testid="legal-tab-refund">
              Refund Policy
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

const chip = (active) =>
  `px-4 py-2 rounded-full text-sm border transition ${
    active
      ? "bg-white text-black border-white"
      : "border-ih-line2 text-white/70 hover:text-white hover:border-white/30"
  }`;
